/**
 * BackgroundCheck — deep BGV workflow inside the Candidate profile.
 *
 * Sections:
 *   1. Progress strip (X/N clear · Y flagged · Z pending)
 *   2. Standard checks — 11 rows, each with status dropdown, assignee,
 *      notes textarea, and doc links (URL-only for MVP; file upload
 *      wires into the existing candidate-assignments infra later).
 *   3. AI Internet-scan plan — Claude generates a targeted search
 *      plan across LinkedIn/GitHub/News/Court records. Deep-linked;
 *      we don't fetch pages ourselves.
 *   4. References — recruiter adds names, schedules a call, records
 *      outcome. AI-generated question set available.
 */
import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ShieldCheck, CheckCircle2, AlertTriangle, Clock, MinusCircle, Sparkles, ExternalLink, RefreshCw, UserPlus, Phone, Trash2, Zap, Settings } from "lucide-react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const STATUS_META = {
    not_started:     { label: "Not started",    cls: "text-muted-foreground border-border bg-white",         icon: Clock },
    in_progress:     { label: "In progress",    cls: "text-blue-800 border-blue-300 bg-blue-50",             icon: Clock },
    clear:           { label: "Clear",          cls: "text-emerald-800 border-emerald-400 bg-emerald-50",    icon: CheckCircle2 },
    flagged:         { label: "Flagged",        cls: "text-rose-800 border-rose-400 bg-rose-50",             icon: AlertTriangle },
    not_applicable:  { label: "N/A",            cls: "text-muted-foreground border-border bg-secondary/40",  icon: MinusCircle },
};

export default function BackgroundCheck({ candidateId }) {
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(null);
    const [aiBusy, setAiBusy] = useState(false);
    const [questionsCache, setQuestionsCache] = useState([]);
    const [questionsBusy, setQuestionsBusy] = useState(false);
    const [vendors, setVendors] = useState([]);
    const [vendorModal, setVendorModal] = useState(null); // {check_key} when unconfigured

    const load = async () => {
        try {
            const { data } = await api.get(`/candidates/${candidateId}/bgv`);
            setData(data);
        } catch (err) { toast.error(formatApiError(err)); }
    };
    useEffect(() => { load(); }, [candidateId]);
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/bgv/vendors`);
                setVendors(data.vendors || []);
            } catch { /* silent — vendor row is optional */ }
        })();
    }, []);

    const trigger = async (checkKey, vendor) => {
        setSaving(checkKey);
        try {
            const { data } = await api.post(`/candidates/${candidateId}/bgv/trigger`, {
                vendor, check_key: checkKey,
            });
            toast.success(`${vendor} triggered — ref ${data.external_id?.slice(0, 8) || "n/a"}`);
            await load();
        } catch (err) {
            const code = err?.response?.data?.detail?.code;
            if (err?.response?.status === 409 && code === "vendor_not_configured") {
                setVendorModal({ check_key: checkKey, vendor: err.response.data.detail.vendor });
            } else {
                toast.error(formatApiError(err));
            }
        }
        finally { setSaving(null); }
    };

    const sync = async (checkKey) => {
        setSaving(checkKey);
        try {
            await api.post(`/candidates/${candidateId}/bgv/${checkKey}/sync`);
            toast.success("Status refreshed");
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(null); }
    };

    const patchCheck = async (key, patch) => {
        setSaving(key);
        try {
            const { data } = await api.patch(`/candidates/${candidateId}/bgv/checks/${key}`, patch);
            setData((d) => ({ ...d, ...data }));
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(null); }
    };

    const runScan = async () => {
        setAiBusy(true);
        try {
            const { data: r } = await api.post(`/candidates/${candidateId}/bgv/internet-scan-plan`);
            setData((d) => ({ ...d, background_check: { ...(d?.background_check || {}), internet_scan: r.internet_scan } }));
            toast.success("Internet scan plan ready");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setAiBusy(false); }
    };

    const runQuestions = async () => {
        setQuestionsBusy(true);
        try {
            const { data: r } = await api.post(`/candidates/${candidateId}/bgv/reference-questions`);
            setQuestionsCache(r?.questions || []);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setQuestionsBusy(false); }
    };

    if (!data) return <div className="text-sm text-muted-foreground p-6">Loading background check…</div>;
    const bgv = data.background_check || {};
    const catalogue = data.catalogue || [];
    const progress = data.progress || { done: 0, total: 0, flagged: 0, pct: 0 };

    return (
        <section className="space-y-6" data-testid="candidate-bgv">
            {/* Header + progress */}
            <div className="border border-border bg-white p-4 flex items-center gap-4 flex-wrap">
                <ShieldCheck className="w-6 h-6 text-ikb shrink-0" />
                <div className="flex-1 min-w-[200px]">
                    <h3 className="font-display font-bold text-lg">Background verification</h3>
                    <p className="text-xs text-muted-foreground" title="Checks marked N/A are excluded from the denominator">
                        {progress.done}/{progress.total} checks complete
                        {progress.flagged > 0 && <span className="text-rose-700 font-medium"> · {progress.flagged} flagged</span>}
                        {bgv.completed_at && <span className="text-emerald-700 font-medium"> · finished {new Date(bgv.completed_at).toLocaleDateString()}</span>}
                    </p>
                </div>
                <div className="flex items-center gap-3 min-w-[200px]">
                    <div className="flex-1 h-2 bg-secondary overflow-hidden">
                        <div
                            className={`h-full transition-all ${progress.flagged > 0 ? "bg-rose-500" : "bg-emerald-500"}`}
                            style={{ width: `${progress.pct}%` }}
                            data-testid="bgv-progress-bar"
                        />
                    </div>
                    <span className="text-sm font-semibold tabular-nums" data-testid="bgv-progress-pct">{progress.pct}%</span>
                </div>
            </div>

            {/* Checks list */}
            <div className="border border-border bg-white">
                <div className="px-4 py-3 border-b border-border bg-secondary/40">
                    <h4 className="font-display font-bold text-base">Standard checks</h4>
                </div>
                <div className="divide-y divide-border">
                    {catalogue.map((c) => (
                        <CheckRow
                            key={c.key}
                            catalog={c}
                            state={(bgv.checks || {})[c.key] || {}}
                            saving={saving === c.key}
                            onPatch={(patch) => patchCheck(c.key, patch)}
                            vendors={vendors}
                            onTrigger={(v) => trigger(c.key, v)}
                            onSync={() => sync(c.key)}
                        />
                    ))}
                </div>
            </div>

            {/* Vendor-not-configured modal */}
            <Dialog open={!!vendorModal} onOpenChange={(o) => { if (!o) setVendorModal(null); }}>
                <DialogContent className="sm:max-w-md" data-testid="bgv-vendor-modal">
                    <DialogHeader>
                        <DialogTitle>Vendor not configured</DialogTitle>
                        <DialogDescription>
                            {vendorModal?.vendor ? (
                                <>
                                    <span className="font-semibold capitalize">{vendorModal.vendor}</span> API keys haven&apos;t
                                    been added to Admin → Integration Keys. Configure them once and every recruiter
                                    can trigger real background checks straight from this panel.
                                </>
                            ) : "This vendor is not configured yet."}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setVendorModal(null)} data-testid="bgv-vendor-modal-cancel">
                            Cancel
                        </Button>
                        <Button asChild data-testid="bgv-vendor-modal-configure">
                            <Link to="/admin/integration-keys" target="_blank">
                                <Settings className="h-4 w-4 mr-1" /> Configure keys →
                            </Link>
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* AI Internet scan */}
            <div className="border border-violet-200 bg-violet-50/40" data-testid="bgv-internet-scan">
                <div className="px-4 py-3 border-b border-violet-200 bg-violet-50 flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-violet-700" />
                        <h4 className="font-display font-bold text-base text-violet-900">Internet scan plan</h4>
                        <span className="text-[11px] text-violet-700/80">AI · Claude Sonnet 4.5</span>
                    </div>
                    <Button
                        onClick={runScan}
                        disabled={aiBusy}
                        variant="outline"
                        className="rounded-none border-violet-300 h-8 text-xs"
                        data-testid="bgv-scan-run-btn"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 mr-1 ${aiBusy ? "animate-spin" : ""}`} />
                        {bgv.internet_scan ? "Regenerate" : "Generate plan"}
                    </Button>
                </div>
                <div className="p-4 space-y-3">
                    {!bgv.internet_scan && !aiBusy && (
                        <p className="text-sm text-foreground/70">
                            Click <b>Generate plan</b> to have AI draft a targeted online-presence review — LinkedIn, GitHub, news mentions, court records, and more. We don&apos;t fetch pages; you click through and form your own judgement.
                        </p>
                    )}
                    {bgv.internet_scan?.summary && (
                        <p className="text-sm text-foreground/80 italic" data-testid="bgv-scan-summary">{bgv.internet_scan.summary}</p>
                    )}
                    {bgv.internet_scan?.search_links?.length > 0 && (
                        <div className="space-y-1">
                            {bgv.internet_scan.search_links.map((sl, i) => (
                                <a
                                    key={i}
                                    href={sl.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-start gap-2 px-3 py-2 border border-border bg-white hover:border-violet-400 hover:bg-violet-50/60 transition-colors group"
                                    data-testid={`bgv-scan-link-${i}`}
                                >
                                    <ExternalLink className="w-4 h-4 mt-0.5 text-muted-foreground group-hover:text-violet-700 shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium">{sl.platform} — <span className="font-mono text-[12px] text-foreground/70">{sl.query}</span></div>
                                        {sl.why && <div className="text-xs text-muted-foreground mt-0.5">{sl.why}</div>}
                                    </div>
                                </a>
                            ))}
                        </div>
                    )}
                    {bgv.internet_scan?.red_flag_signals?.length > 0 && (
                        <div className="mt-2 border border-rose-200 bg-rose-50/40 p-3">
                            <div className="text-xs font-semibold text-rose-900 mb-1">Watch for:</div>
                            <ul className="text-xs text-rose-950 list-disc pl-5 space-y-0.5">
                                {bgv.internet_scan.red_flag_signals.map((s, i) => <li key={i}>{s}</li>)}
                            </ul>
                        </div>
                    )}
                </div>
            </div>

            {/* References */}
            <ReferencesSection
                candidateId={candidateId}
                references={bgv.references || []}
                onChanged={load}
                questionsCache={questionsCache}
                questionsBusy={questionsBusy}
                onGenerateQuestions={runQuestions}
            />
        </section>
    );
}

function CheckRow({ catalog, state, saving, onPatch, vendors = [], onTrigger, onSync }) {
    const status = state.status || "not_started";
    const meta = STATUS_META[status] || STATUS_META.not_started;
    const Icon = meta.icon;
    const [pickVendor, setPickVendor] = useState(false);
    const trig = state.vendor_trigger;
    return (
        <div className="p-4 grid grid-cols-1 md:grid-cols-[220px_180px_1fr] gap-3 items-start" data-testid={`bgv-check-${catalog.key}`}>
            <div>
                <div className="text-sm font-semibold flex items-center gap-1.5">
                    <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                    {catalog.label}
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">{catalog.desc}</div>
                {state.updated_by && (
                    <div className="text-[10px] text-muted-foreground/80 mt-1">
                        by {state.updated_by} · {state.updated_at ? new Date(state.updated_at).toLocaleDateString() : ""}
                    </div>
                )}
                {trig && (
                    <div className="text-[10px] mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 border border-ikb/40 text-ikb bg-ikb/5">
                        <Zap className="w-3 h-3" />
                        {trig.vendor} · {trig.external_id?.slice(0, 12) || "no ref"}
                    </div>
                )}
            </div>
            <div>
                <select
                    value={status}
                    onChange={(e) => onPatch({ status: e.target.value })}
                    disabled={saving}
                    className={`w-full h-9 px-2 border text-sm ${meta.cls} rounded-none`}
                    data-testid={`bgv-status-${catalog.key}`}
                >
                    {Object.entries(STATUS_META).map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                    ))}
                </select>
                {vendors.length > 0 && !trig && (
                    <div className="mt-1.5 relative">
                        <button
                            type="button"
                            onClick={() => setPickVendor((v) => !v)}
                            className="w-full h-7 border border-border text-[11px] hover:bg-secondary inline-flex items-center justify-center gap-1"
                            data-testid={`bgv-trigger-${catalog.key}`}
                        >
                            <Zap className="w-3 h-3" /> Trigger via vendor
                        </button>
                        {pickVendor && (
                            <div className="absolute z-10 top-full left-0 right-0 bg-white border border-border mt-0.5 shadow-md">
                                {vendors.map((v) => (
                                    <button
                                        key={v.vendor}
                                        onClick={() => { setPickVendor(false); onTrigger(v.vendor); }}
                                        className="w-full px-2 py-1.5 text-left text-[11px] hover:bg-secondary flex items-center justify-between"
                                        data-testid={`bgv-trigger-${catalog.key}-${v.vendor}`}
                                    >
                                        <span>{v.label}</span>
                                        {v.configured ? (
                                            <span className="text-emerald-700 text-[9px]">✓ ready</span>
                                        ) : (
                                            <span className="text-muted-foreground text-[9px]">not set</span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
                {trig && (
                    <button
                        type="button"
                        onClick={onSync}
                        disabled={saving}
                        className="mt-1.5 w-full h-7 border border-border text-[11px] hover:bg-secondary inline-flex items-center justify-center gap-1"
                        data-testid={`bgv-sync-${catalog.key}`}
                    >
                        <RefreshCw className={`w-3 h-3 ${saving ? "animate-spin" : ""}`} /> Sync
                    </button>
                )}
            </div>
            <div>
                <Textarea
                    rows={1}
                    defaultValue={state.notes || ""}
                    onBlur={(e) => { if (e.target.value !== (state.notes || "")) onPatch({ notes: e.target.value }); }}
                    placeholder="Notes, vendor ref, doc URL…"
                    className="rounded-none text-sm min-h-[36px]"
                    style={{ fieldSizing: "content" }}
                    data-testid={`bgv-notes-${catalog.key}`}
                />
            </div>
        </div>
    );
}

function ReferencesSection({ candidateId, references, onChanged, questionsCache, questionsBusy, onGenerateQuestions }) {
    const [form, setForm] = useState({ name: "", relationship: "", company: "", phone: "", email: "", scheduled_at: "", outcome: "pending", notes: "" });
    const [busy, setBusy] = useState(false);

    const add = async () => {
        if (!form.name.trim()) { toast.error("Reference name is required"); return; }
        setBusy(true);
        try {
            await api.post(`/candidates/${candidateId}/bgv/references`, form);
            toast.success("Reference added");
            setForm({ name: "", relationship: "", company: "", phone: "", email: "", scheduled_at: "", outcome: "pending", notes: "" });
            onChanged?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const patch = async (id, patchObj) => {
        try {
            await api.patch(`/candidates/${candidateId}/bgv/references/${id}`, patchObj);
            onChanged?.();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this reference?")) return;
        try {
            await api.delete(`/candidates/${candidateId}/bgv/references/${id}`);
            onChanged?.();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="border border-border bg-white" data-testid="bgv-references">
            <div className="px-4 py-3 border-b border-border bg-secondary/40 flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    <h4 className="font-display font-bold text-base">Reference calls</h4>
                </div>
                <Button
                    onClick={onGenerateQuestions}
                    disabled={questionsBusy}
                    variant="outline"
                    className="rounded-none h-8 text-xs"
                    data-testid="bgv-ref-questions-btn"
                >
                    <Sparkles className={`w-3.5 h-3.5 mr-1 ${questionsBusy ? "animate-pulse" : ""}`} />
                    AI-generate questions
                </Button>
            </div>
            {questionsCache?.length > 0 && (
                <div className="px-4 py-3 border-b border-border bg-violet-50/40">
                    <div className="text-xs font-semibold text-violet-900 mb-1">Suggested reference-call questions:</div>
                    <ol className="text-sm text-foreground/80 list-decimal pl-5 space-y-1">
                        {questionsCache.map((q, i) => <li key={i}>{q}</li>)}
                    </ol>
                </div>
            )}
            <div className="p-4 space-y-3">
                {references.map((r) => (
                    <ReferenceRow key={r.id} r={r} onPatch={(p) => patch(r.id, { ...r, ...p })} onRemove={() => remove(r.id)} />
                ))}
                {references.length === 0 && (
                    <div className="text-xs text-muted-foreground italic">No references yet. Add 2-3 former managers/peers below.</div>
                )}
                <div className="border-t border-border pt-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                        <Input placeholder="Name*" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none h-9" data-testid="bgv-ref-name" />
                        <Input placeholder="Relationship (e.g. former manager)" value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })} className="rounded-none h-9" data-testid="bgv-ref-relationship" />
                        <Input placeholder="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="rounded-none h-9" data-testid="bgv-ref-company" />
                        <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-none h-9" data-testid="bgv-ref-phone" />
                        <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none h-9" data-testid="bgv-ref-email" />
                        <Input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} className="rounded-none h-9" data-testid="bgv-ref-scheduled" />
                    </div>
                    <Button onClick={add} disabled={busy} className="rounded-none mt-2 h-9 text-xs" data-testid="bgv-ref-add-btn">
                        <UserPlus className="w-3.5 h-3.5 mr-1" /> Add reference
                    </Button>
                </div>
            </div>
        </div>
    );
}

function ReferenceRow({ r, onPatch, onRemove }) {
    return (
        <div className="border border-border p-3 grid grid-cols-1 md:grid-cols-[1.5fr_1fr_1fr_auto] gap-2 items-center" data-testid={`bgv-ref-row-${r.id}`}>
            <div>
                <div className="text-sm font-semibold">{r.name}</div>
                <div className="text-xs text-muted-foreground">{[r.relationship, r.company].filter(Boolean).join(" · ")}</div>
                <div className="text-[11px] text-muted-foreground/80 mt-0.5">
                    {r.phone && <>📞 {r.phone} </>}
                    {r.email && <>· ✉︎ {r.email}</>}
                </div>
            </div>
            <div>
                <Label className="text-[10px] text-muted-foreground">Scheduled</Label>
                <Input
                    type="datetime-local"
                    defaultValue={r.scheduled_at || ""}
                    onBlur={(e) => { if (e.target.value !== (r.scheduled_at || "")) onPatch({ scheduled_at: e.target.value }); }}
                    className="rounded-none h-8 text-xs"
                />
            </div>
            <div>
                <Label className="text-[10px] text-muted-foreground">Outcome</Label>
                <select
                    value={r.outcome || "pending"}
                    onChange={(e) => onPatch({ outcome: e.target.value })}
                    className="w-full h-8 px-2 border border-border text-sm rounded-none bg-white"
                >
                    <option value="pending">Pending</option>
                    <option value="positive">Positive</option>
                    <option value="negative">Negative</option>
                    <option value="no_show">No-show</option>
                    <option value="unreachable">Unreachable</option>
                </select>
            </div>
            <button type="button" onClick={onRemove} className="text-muted-foreground hover:text-rose-700" title="Delete reference">
                <Trash2 className="w-4 h-4" />
            </button>
        </div>
    );
}
